import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import versions from '../content/legalVersions.json';
import { AFTERRA_URL, DISCORD_INVITE_URL } from '../config/community';
import { DiscordGlyph } from '../components/site/SiteFooter';

const external = { target: '_blank', rel: 'noopener noreferrer' };
const A = ({ href, children }) => <a href={href} {...external} className="text-[hsl(var(--mg-purple))] font-medium hover:underline">{children}</a>;
const L = ({ to, children }) => <Link to={to} className="text-[hsl(var(--mg-purple))] font-medium hover:underline">{children}</Link>;

function Section({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-24 space-y-3">
      <h2 className="font-heading text-xl sm:text-2xl font-bold">{title}</h2>
      {children}
    </section>
  );
}

const LIVE = [
  'Profiles, posts, photos and Sagas (short videos), stories, comments, likes, reposts and Keepsakes',
  'Ravenpost private messages and notifications, including optional push notifications',
  'Runegold: balances, transfers between members, donations to creators',
  'oDIN, an AI guide to the world, its lore and its people',
  'The Realms map: Midgard is open, with Thornhaven and Frostholm ready for travellers',
];
const BUILDING = [
  'The explorable 3D world of Thornhaven and beyond, still in development',
  'The other eight realms, already on the map and not yet playable',
  'A referral programme (see below)',
  'A native mobile app; today Midgard installs from the browser',
];

const FAQ = [
  ['Is Midgard free?', 'Yes. Creating an account and using Midgard is free. Some optional extras, such as Premium, advertising or buying Runegold, cost real money and always show their price first.'],
  ['Can I cash out Runegold?', 'No. Runegold only exists inside Midgard and has no cash value. Any future change would need its own rules and a legal review first.'],
  ['Who can see what I post?', 'Posts and profiles are visible to other members, and a preview appears when someone shares a link. Ravenpost messages are only between you and the person you write to.'],
  ['Is oDIN a person?', 'No. oDIN is an AI system. The oDIN & AI Notice explains which services help him answer.'],
  ['Can I install Midgard on my phone?', 'Yes. Open midgard.one in your browser and choose "Install app" or "Add to Home screen".'],
  ['How do I delete my account?', 'Contact the team (see Support below) and we will close the account and delete your data. A self-service option is planned.'],
];

export default function AboutPage() {
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    const el = document.getElementById(hash.slice(1));
    if (el) el.scrollIntoView({ block: 'start' });
  }, [hash]);

  return (
    <article className="mg-page max-w-3xl py-6 sm:py-10 space-y-12 leading-relaxed text-[15px]" data-testid="about-page">
      <header>
        <p className="text-[12px] uppercase tracking-[0.18em] text-muted-foreground">About</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold mt-2">Midgard</h1>
        <p className="mt-4 text-lg text-foreground/90">
          A Norse-inspired social universe where people meet, tell stories, build who they are and explore a world that keeps growing.
        </p>
        <p className="mt-3 text-muted-foreground">
          It starts like a social network you already know: a home feed, profiles, messages, short videos. Around it sits a world
          with realms, settlements and an oracle, and the two are meant to become one place.
        </p>
      </header>

      <Section id="midgard" title="What is here today">
        <ul className="list-disc pl-5 space-y-1.5">{LIVE.map((x) => <li key={x}>{x}</li>)}</ul>
        <h3 className="font-heading font-bold pt-2">Still being built</h3>
        <ul className="list-disc pl-5 space-y-1.5 text-muted-foreground">{BUILDING.map((x) => <li key={x}>{x}</li>)}</ul>
      </Section>

      <Section id="origins" title="Origins">
        <p>
          Midgard was conceived, created and is being developed by <A href={AFTERRA_URL}>AFTERRA</A>. AFTERRA designs the world,
          writes and builds the platform, and decides where it goes next, with the community's help.
        </p>
      </Section>

      <Section id="komplex" title="The AFTERRA Komplex">
        <p>
          AFTERRA is a creative studio that builds websites, visual identities and digital presences. The Komplex is the wider body
          of that work, and Midgard is the studio's own long-term world. Each keeps its own identity: AFTERRA's is clean and
          contemporary, Midgard's belongs to the Nine Realms.
        </p>
        <p><A href={AFTERRA_URL}>Visit afterra.one</A></p>
      </Section>

      <Section id="community" title="Community">
        <p>
          Midgard is shaped by the people who use it. Members test new features, point out what breaks, suggest what should come
          next and help keep the halls friendly. The Wardens, Midgard's moderators, review reports and look after the Community Guidelines.
        </p>
        <div className="mg-card-elevated p-5">
          <h3 className="font-heading font-bold text-lg">Join the Midgard Community</h3>
          <p className="mt-1 text-muted-foreground">
            Meet fellow adventurers, share your ideas, report bugs and help shape the future of Midgard. Active contributors may
            earn Runegold for verified bug reports.
          </p>
          <a href={DISCORD_INVITE_URL} {...external}
            className="mt-4 inline-flex items-center gap-2 h-11 px-5 rounded-lg font-semibold text-white bg-[#5865F2] hover:bg-[#4752c4] transition-colors"
            data-testid="about-discord">
            <DiscordGlyph className="w-5 h-5" /> Join Our Discord
          </a>
          <p className="mt-3 text-[13px] text-muted-foreground">
            Bug rewards are reviewed by hand: verified reports may receive 1 to 5 Runegold, at the team's discretion.
            Full rules: <L to="/legal/runegold#bug-report-rewards">Runegold &amp; Rewards</L>.
          </p>
        </div>
      </Section>

      <Section id="referral" title="Referral programme · Coming soon">
        <p>
          We plan to thank members who bring friends into Midgard. How it will work, and what it will offer, has not been decided,
          and nothing is earned through referrals yet. Its rules will be published here before it starts.
        </p>
      </Section>

      <Section id="faq" title="Questions">
        <dl className="space-y-4">
          {FAQ.map(([q, a]) => (
            <div key={q}>
              <dt className="font-semibold">{q}</dt>
              <dd className="text-muted-foreground mt-1">{a}</dd>
            </div>
          ))}
        </dl>
      </Section>

      <Section id="status" title="Development status">
        <p>
          Midgard is live and in active development. Features change as we learn, and parts of the world are clearly marked when
          they are not open yet. Larger changes are announced on Discord.
        </p>
      </Section>

      <Section id="support" title="Support and contact">
        <p>
          The quickest way to reach the team is our <A href={DISCORD_INVITE_URL}>Discord</A>. To report content or an account,
          use the report option on the post, Saga, comment or profile itself so the Wardens see it directly.
        </p>
        <p className="text-muted-foreground">
          For privacy requests (a copy of your data, corrections or deletion), contact the team the same way; a dedicated
          contact address will be published here and in the Privacy Policy.
        </p>
      </Section>

      <Section id="expectations" title="What we expect from each other">
        <p>
          Be decent, be honest, and leave room for others. The <L to="/legal/guidelines">Community Guidelines</L> explain what
          that means in practice and what happens when someone crosses the line.
        </p>
      </Section>

      <Section id="accessibility" title="Accessibility">
        <p>
          Midgard aims to work with keyboards, screen readers, browser zoom and reduced-motion settings, on phones and desktops.
          Some parts, especially the artwork maps and the 3D world, are not fully accessible yet. If something stops you, tell us on
          Discord and we will prioritise it.
        </p>
      </Section>

      <Section id="legal" title="Legal and privacy">
        <p className="text-muted-foreground">
          {versions.status === 'draft' && 'These documents are drafts under review and not yet in force. '}
          They describe how Midgard works and what applies to everyone who uses it.
        </p>
        <ul className="grid sm:grid-cols-2 gap-2">
          {Object.entries(versions.documents).map(([id, d]) => (
            <li key={id}><L to={`/legal/${id}`}>{d.title}</L></li>
          ))}
          <li><L to="/legal/cookies#preferences">Manage cookie preferences</L></li>
        </ul>
      </Section>
    </article>
  );
}
